import '@livekit/components-styles';
import { LiveKitRoom, VideoTrack, AudioTrack, useTracks, useRoomContext } from '@livekit/components-react';
import { Track } from 'livekit-client';
import type { TrackReference } from '@livekit/components-react';
import { Box, Paper, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';

export interface LiveKitPublishPreviewProps {
  /** LiveKit server URL */
  serverUrl: string;
  /** JWT token for room connection (publish mode) */
  token: string;
  /** Enable camera. Default true. */
  enableCamera?: boolean;
  /** Enable microphone. Default true. */
  enableMicrophone?: boolean;
  /** Enable screenshare. Default false. */
  enableScreenshare?: boolean;
  /** Callback when connection fails */
  onError?: (error: Error) => void;
  /** Callback when connected */
  onConnected?: () => void;
  /** Custom class name for styling */
  className?: string;
  /** Height of the preview area */
  height?: string | number;
}

/**
 * Joins a LiveKit room and publishes local camera/mic/screenshare.
 * Reusable for: testing publish before pairing, contributors publishing as remote speakers from dashboard.
 */
export function LiveKitPublishPreview({
  serverUrl,
  token,
  enableCamera = true,
  enableMicrophone = true,
  enableScreenshare = false,
  onError,
  onConnected,
  className,
  height = '100%',
}: LiveKitPublishPreviewProps) {
  return (
    <Box sx={{ height, width: '100%', position: 'relative' }} className={className}>
      <LiveKitRoom
        video={enableCamera}
        audio={enableMicrophone}
        screen={enableScreenshare}
        connect={true}
        token={token}
        serverUrl={serverUrl}
        onError={onError}
        onConnected={onConnected}
        style={{ height: '100%', width: '100%' }}
      >
        <PublishPreviewContent />
      </LiveKitRoom>
    </Box>
  );
}

function PublishPreviewContent() {
  const room = useRoomContext();

  useEffect(() => {
    if (!room) return;
    room.localParticipant.setMicrophoneEnabled(true).catch(() => {});
    room.localParticipant.setCameraEnabled(true).catch(() => {});
    return () => {};
  }, [room]);

  const allTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.Microphone, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.ScreenShareAudio, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const localTracks = useMemo(
    () => allTracks.filter((t): t is TrackReference => !!t.publication?.trackSid && t.participant.isLocal),
    [allTracks],
  );

  const videoTracks = useMemo(
    () => localTracks.filter((t) => t.source !== Track.Source.Microphone && t.source !== Track.Source.ScreenShareAudio),
    [localTracks],
  );

  const audioTracks = useMemo(
    () => localTracks.filter((t) => t.source === Track.Source.Microphone || t.source === Track.Source.ScreenShareAudio),
    [localTracks],
  );

  if (videoTracks.length === 0 && audioTracks.length === 0) {
    return (
      <Paper
        elevation={2}
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          bgcolor: '#1a1a1a',
        }}
      >
        <Typography variant="body1" color="text.secondary" align="center">
          Requesting camera and microphone...
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Please allow camera and microphone access when prompted
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#000',
        overflow: 'hidden',
      }}
    >
      {audioTracks.map((trackRef) => (
        <AudioTrack key={`${trackRef.participant.identity}-${trackRef.publication?.trackSid}`} trackRef={trackRef} />
      ))}
      {videoTracks.length > 0 && (
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {videoTracks.map((trackRef) => (
            <Box
              key={`${trackRef.participant.identity}-${trackRef.publication?.trackSid}`}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <VideoTrack trackRef={trackRef} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
